import { z } from "zod";
import { businessRoleSchema } from "../enums.ts";
import { isoDateTimeSchema, uuidSchema } from "./common.ts";
import { adminBillingSummarySchema } from "./subscription.ts";
import { heardFromSchema } from "../signup-source.ts";

/**
 * The Control Center read views (control-center-split.md), first built as the Desk admin area
 * (web-app-v2.md section 5). Read-only cross-tenant views for the Teamna team. They are served only
 * by the Control Center API, behind a verified @teamna.app Google session, never by Desk.
 */

// --- the staff session -------------------------------------------------------

/** `GET /v1/me` on the Control Center API: the signed-in Teamna staff member. */
export const staffMeResponseSchema = z.object({
  email: z.string().email(),
  name: z.string().nullable(),
});
export type StaffMeResponse = z.infer<typeof staffMeResponseSchema>;

// --- businesses --------------------------------------------------------------

/**
 * Where the business came from (task 0.7): the sign-up answer, the referrer name, the website
 * campaign values, and the Teamna staff tag. All null on a business created before the question.
 */
export const adminSignupSourceSchema = z.object({
  /** "How did you hear about Teamna?". Null: created before the question, or by a client that does not ask it. */
  heardFrom: heardFromSchema.nullable(),
  /** The owner who told them, as typed. Only with `heardFrom: another_owner`. */
  referrerName: z.string().nullable(),
  utmSource: z.string().nullable(),
  utmMedium: z.string().nullable(),
  utmCampaign: z.string().nullable(),
  /** The "Warm intro" tag, set by Teamna staff in Control Center. */
  warmIntroTag: z.boolean(),
  /** The rule (`isWarmIntro`): the answer is "Anas or Lana", or the tag is on. */
  warmIntro: z.boolean(),
});
export type AdminSignupSource = z.infer<typeof adminSignupSourceSchema>;

export const adminBusinessSummarySchema = z.object({
  id: uuidSchema,
  name: z.string(),
  slug: z.string(),
  sport: z.string(),
  /** The owner row's name, null when the owner has not set one. Null when the business has no owner. */
  ownerName: z.string().nullable(),
  /** The owner's mobile number or email, whichever the row holds. Null when the business has no owner. */
  ownerIdentifier: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  /** Setup steps done, out of `setupTotal`, the same eight the owner sees on Home. */
  setupDone: z.number().int(),
  setupTotal: z.number().int(),
  /** Memberships with status `active`. */
  membersActive: z.number().int(),
  /** Participants attached to the business and not archived. */
  participants: z.number().int(),
  /** People who run the business: owner, admins and staff. */
  staffCount: z.number().int(),
  /** The newest audit row for the business, null when there is none yet. */
  lastActivityAt: isoDateTimeSchema.nullable(),
  /** The Teamna plan (subscription billing). Null for a demo, a pilot, or before the subscription exists. */
  billing: adminBillingSummarySchema.nullable(),
  /** Set only on a demo club spawned from Control Center: when it is destroyed. Null for a real business. */
  demoExpiresAt: isoDateTimeSchema.nullable(),
  /** Set while the business is in the Teamna pilot: no trial, no charge. `billing` is then null. */
  pilotSince: isoDateTimeSchema.nullable(),
  signupSource: adminSignupSourceSchema,
});
export type AdminBusinessSummary = z.infer<typeof adminBusinessSummarySchema>;

/** `GET /v1/admin/businesses` */
export const adminBusinessesResponseSchema = z.object({
  items: z.array(adminBusinessSummarySchema),
});
export type AdminBusinessesResponse = z.infer<typeof adminBusinessesResponseSchema>;

export const adminTeamMemberSchema = z.object({
  userId: uuidSchema,
  name: z.string().nullable(),
  role: businessRoleSchema,
});
export type AdminTeamMember = z.infer<typeof adminTeamMemberSchema>;

export const adminGroupSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: z.string(),
  /** Active schedule templates on the group. */
  slotsPerWeek: z.number().int(),
});
export type AdminGroup = z.infer<typeof adminGroupSchema>;

export const adminPackageSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  sessions: z.number().int(),
  priceFils: z.number().int(),
  active: z.boolean(),
});
export type AdminPackage = z.infer<typeof adminPackageSchema>;

export const adminAuditRowSchema = z.object({
  id: uuidSchema,
  action: z.string(),
  actorName: z.string().nullable(),
  /** The Teamna staff email, for a change made in Control Center. Null otherwise. */
  actorStaffEmail: z.string().nullable(),
  createdAt: isoDateTimeSchema,
  entity: z.string(),
  entityId: uuidSchema.nullable(),
});
export type AdminAuditRow = z.infer<typeof adminAuditRowSchema>;

/** `GET /v1/admin/businesses/{id}` */
export const adminBusinessDetailSchema = adminBusinessSummarySchema.extend({
  timezone: z.string(),
  team: z.array(adminTeamMemberSchema),
  groups: z.array(adminGroupSchema),
  packages: z.array(adminPackageSchema),
  /** Card payments that succeeded in the current month of the business timezone. */
  paymentsThisMonth: z.object({
    cardTotalFils: z.number().int(),
    cardCount: z.number().int(),
  }),
  /** Payment requests still pending or overdue. */
  openPaymentRequests: z.object({
    count: z.number().int(),
    totalFils: z.number().int(),
  }),
  /** The last 20 audit rows, newest first. */
  audit: z.array(adminAuditRowSchema),
});
export type AdminBusinessDetail = z.infer<typeof adminBusinessDetailSchema>;

/** `POST /v1/admin/businesses/{id}/delete`: deletes the business and everything in it. */
export const deleteBusinessRequestSchema = z.object({
  /** The business name, typed by the staff member. Must match exactly. */
  confirmName: z.string().min(1).max(200),
});
export type DeleteBusinessRequest = z.infer<typeof deleteBusinessRequestSchema>;

export const deleteBusinessResponseSchema = z.object({
  deleted: z.literal(true),
  /** True when a live Teamna subscription was cancelled at the provider before the delete. */
  subscriptionCancelled: z.boolean(),
});
export type DeleteBusinessResponse = z.infer<typeof deleteBusinessResponseSchema>;

/** `POST /v1/admin/businesses/{id}/warm-intro`: the "Warm intro" tag on or off. Answers the new source. */
export const updateWarmIntroTagRequestSchema = z.object({ on: z.boolean() });
export type UpdateWarmIntroTagRequest = z.infer<typeof updateWarmIntroTagRequestSchema>;

// --- health ------------------------------------------------------------------

/** `GET /v1/admin/health` */
export const adminHealthResponseSchema = z.object({
  /** pg-boss job counts by state. Null when the pgboss schema is not there or cannot be read. */
  queue: z
    .object({
      created: z.number().int(),
      active: z.number().int(),
      failed: z.number().int(),
    })
    .nullable(),
  /** Why `queue` is null, else null. */
  queueUnavailableReason: z.string().nullable(),
  /** Webhook events received in the last 24 hours that were never marked processed. */
  webhookEventsFailed24h: z.number().int(),
  /** Sign-in codes from the last 24 hours that a real channel was asked to carry and did not. */
  signInCodesUndelivered24h: z.number().int(),
  sessionGenerator: z.object({
    /** Newest session created from a schedule template. Null before the first run. */
    lastSessionCreatedAt: isoDateTimeSchema.nullable(),
    /** Completion time of the last pg-boss `generate-sessions` job, when the queue is readable. */
    lastJobCompletedAt: isoDateTimeSchema.nullable(),
  }),
  databaseNow: isoDateTimeSchema,
});
export type AdminHealthResponse = z.infer<typeof adminHealthResponseSchema>;

// --- demo clubs --------------------------------------------------------------

/** The sports a demo club can be spawned with. Each fills the club from its sport template. */
export const demoSports = ["swimming", "pilates"] as const;
export const demoSportSchema = z.enum(demoSports);
export type DemoSport = z.infer<typeof demoSportSchema>;

/** The three roles a demo club hands out as logins. */
export const demoRoles = ["owner", "coach", "parent"] as const;
export const demoRoleSchema = z.enum(demoRoles);
export type DemoRole = z.infer<typeof demoRoleSchema>;

/** One login of a demo club. It signs in only with a magic link (`POST /v1/admin/demos/:id/links`). */
export const adminDemoAccountSchema = z.object({
  userId: uuidSchema,
  role: demoRoleSchema,
  name: z.string(),
});
export type AdminDemoAccount = z.infer<typeof adminDemoAccountSchema>;

export const adminDemoClubSchema = z.object({
  businessId: uuidSchema,
  name: z.string(),
  sport: z.string(),
  createdAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
  createdByStaffEmail: z.string(),
  /** Owner, coach and parent, in that order. */
  accounts: z.array(adminDemoAccountSchema),
  /** Other families in the club, so its lists are not empty. They are not logins. */
  extraFamilies: z.number().int(),
});
export type AdminDemoClub = z.infer<typeof adminDemoClubSchema>;

/** `GET /v1/admin/demos`, newest first. */
export const adminDemoClubsResponseSchema = z.object({
  items: z.array(adminDemoClubSchema),
});
export type AdminDemoClubsResponse = z.infer<typeof adminDemoClubsResponseSchema>;

/** `POST /v1/admin/demos` */
export const createDemoClubRequestSchema = z.object({
  sport: demoSportSchema,
});
export type CreateDemoClubRequest = z.infer<typeof createDemoClubRequestSchema>;

/** `POST /v1/admin/demos/:id/links`: email a magic link for one login to a Teamna team address. */
export const sendDemoLinkRequestSchema = z.object({
  role: demoRoleSchema,
  /** A @teamna.app address. Defaults to the staff member who asks. */
  to: z.string().email().optional(),
});
export type SendDemoLinkRequest = z.infer<typeof sendDemoLinkRequestSchema>;

export const sendDemoLinkResponseSchema = z.object({
  sentTo: z.string(),
  expiresAt: isoDateTimeSchema,
  /** Only where no email provider is configured (a local run): the link itself, since nothing was sent. */
  url: z.string().optional(),
});
export type SendDemoLinkResponse = z.infer<typeof sendDemoLinkResponseSchema>;

/**
 * Control Center, Teamna-wide switches. `cardPayments.on` false shows "Coming soon" to owners for
 * every card payment part and refuses card checkouts for all businesses. Manual payments (cash,
 * bank transfer) are not affected.
 */
const platformSwitchSchema = z.object({
  on: z.boolean(),
  /** Null while nobody has changed the switch (it is then on). */
  updatedAt: z.string().datetime().nullable(),
  updatedByStaffEmail: z.string().nullable(),
});

/**
 * `ownerSignUp.on` false is early access only: a new business needs a number or an email on the
 * allow list (Control Center, Early access), and the website shows "Ask for early access".
 */
export const platformSettingsResponseSchema = z.object({
  cardPayments: platformSwitchSchema,
  ownerSignUp: platformSwitchSchema,
});
export type PlatformSettingsResponse = z.infer<typeof platformSettingsResponseSchema>;

/** The body of every Teamna-wide switch change. */
export const updateCardPaymentsSwitchRequestSchema = z.object({ on: z.boolean() });
export type UpdateCardPaymentsSwitchRequest = z.infer<typeof updateCardPaymentsSwitchRequestSchema>;

// --- Early access (Control Center) -------------------------------------------

export const adminSignUpAllowanceSchema = z.object({
  id: uuidSchema,
  phoneE164: z.string().nullable(),
  email: z.string().nullable(),
  waitlistSignupId: uuidSchema.nullable(),
  allowedByStaffEmail: z.string(),
  createdAt: isoDateTimeSchema,
  /** When the person created their business. Null until then. */
  usedAt: isoDateTimeSchema.nullable(),
});
export type AdminSignUpAllowance = z.infer<typeof adminSignUpAllowanceSchema>;

/** One early access form, as Control Center lists it. The contact is the full number: staff reply on WhatsApp. */
export const adminEarlyAccessRequestSchema = z.object({
  id: uuidSchema,
  createdAt: isoDateTimeSchema,
  businessName: z.string(),
  contactName: z.string(),
  /** E.164 for every form since 2026-10-21; an older row can hold an email. */
  contact: z.string(),
  email: z.string().nullable(),
  businessType: z.string(),
  role: z.string(),
  websiteUrl: z.string().nullable(),
  instagram: z.string().nullable(),
  memberCount: z.string().nullable(),
  currentTools: z.string().nullable(),
  utmSource: z.string().nullable(),
  utmCampaign: z.string().nullable(),
  allowance: adminSignUpAllowanceSchema.nullable(),
});
export type AdminEarlyAccessRequest = z.infer<typeof adminEarlyAccessRequestSchema>;

export const adminEarlyAccessResponseSchema = z.object({
  /** False: early access only. The same value as `ownerSignUp.on` in the platform settings. */
  signUpOpen: z.boolean(),
  requests: z.array(adminEarlyAccessRequestSchema),
  /** Numbers and emails that staff added by hand, with no form. */
  otherAllowances: z.array(adminSignUpAllowanceSchema),
});
export type AdminEarlyAccessResponse = z.infer<typeof adminEarlyAccessResponseSchema>;

/** Allow from a form (`waitlistSignupId`), or from a number or an email that staff type. */
export const allowSignUpRequestSchema = z.union([
  z.object({ waitlistSignupId: uuidSchema }),
  z
    .object({ phone: z.string().trim().max(40).optional(), email: z.string().trim().email().max(200).optional() })
    .refine((value) => Boolean(value.phone || value.email), { message: "Give a mobile number or an email" }),
]);
export type AllowSignUpRequest = z.infer<typeof allowSignUpRequestSchema>;

/**
 * Control Center, one business: the "Runs on Teamna" link that parents see on the join page, the
 * payment page, the receipt and the booking emails (website traffic plan, task 2.1).
 */
export const adminRunsOnTeamnaSchema = z.object({
  /** False when Teamna staff turned the link off for this business. On is the default. */
  on: z.boolean(),
  /** The business has a paid branded app: the link is off on every surface, whatever `on` says. */
  brandedApp: z.boolean(),
});
export type AdminRunsOnTeamna = z.infer<typeof adminRunsOnTeamnaSchema>;

export const updateRunsOnTeamnaRequestSchema = z.object({ on: z.boolean() });
export type UpdateRunsOnTeamnaRequest = z.infer<typeof updateRunsOnTeamnaRequestSchema>;

// --- costs -------------------------------------------------------------------

/** A calendar month, `YYYY-MM`, in UTC (Twilio counts its days in UTC). */
/** `YYYY-MM` in this century: `Date.UTC` reads the years 0 to 99 as 1900 to 1999. */
export const costMonthSchema = z.string().regex(/^20\d{2}-(0[1-9]|1[0-2])$/, "Use YYYY-MM");

/** `GET /v1/admin/costs?month=` */
export const adminCostsQuerySchema = z.object({ month: costMonthSchema.optional() });

/** A dollar amount from Twilio, or why it cannot be read. */
const twilioAmountSchema = z.union([
  z.object({ usd: z.number() }),
  z.object({ unavailable: z.string() }),
]);

export const adminCostRowSchema = z.object({
  /** Null for the row of costs that belong to no business (sign-ins of people with no business). */
  businessId: uuidSchema.nullable(),
  businessName: z.string().nullable(),
  /** AI Assistant answers plus the other model calls, in millionths of a US dollar. */
  aiMicros: z.number().int(),
  aiCalls: z.number().int(),
  /** This business's share of the month's Twilio bill. Null when the bill cannot be read. */
  twilioMicros: z.number().int().nullable(),
  /** Phone sign-ins of the people of the business (codes by SMS or WhatsApp, WhatsApp sign-ins). */
  phoneSignIns: z.number().int(),
});
export type AdminCostRow = z.infer<typeof adminCostRowSchema>;

export const adminCostsSchema = z.object({
  month: costMonthSchema,
  /** What is left on the Twilio account now (not at the end of the month). */
  twilioBalance: twilioAmountSchema,
  /** Below this the balance tile is red and Slack gets a notice each day. */
  lowBalanceUsd: z.number(),
  /** The month's Twilio bill. */
  twilioTotal: twilioAmountSchema,
  aiTotalMicros: z.number().int(),
  /** Highest total cost first; the no-business row is last. */
  rows: z.array(adminCostRowSchema),
});
export type AdminCosts = z.infer<typeof adminCostsSchema>;
